/**
 * Authenticated app layout — parent route for /app/*.
 *
 * Gate: if no session, redirect to /auth/login. While AuthProvider is
 * hydrating (`loading: true`) we render nothing so we don't flash a
 * redirect before the session is known.
 *
 * MFA step-up gate: once a session is present, we ask Supabase whether the
 * user has a verified TOTP factor that the current session has not yet
 * stepped up to (currentLevel === 'aal1' && nextLevel === 'aal2'). If so we
 * redirect to /auth/mfa-verify so the user can promote the session to aal2
 * before the protected app surface renders. While the AAL check is in
 * flight we render nothing — same rationale as the session-loading branch.
 *
 * Fail-CLOSED (2026-05-25 audit): if the AAL check itself errors we redirect
 * to /auth/mfa-verify rather than rendering the app. Server-side aal2
 * enforcement on sensitive operations is the real backstop, so erring toward
 * the verify screen is safe and avoids silently dropping the client-side gate
 * on a transient error. mfa-verify itself handles the "no factor enrolled"
 * case, so a user without TOTP isn't trapped.
 *
 * Act-as exception (2026-07-02, QA P1): when a firm is administering a client
 * via an ACTIVE act-as context (firm-act store), the effective credential on
 * /app is the short-lived act-as token, NOT the operator's own Supabase
 * session. Minting that token already required a stepped-up operator server-
 * side, so re-running the OPERATOR-session AAL gate here is both redundant and
 * wrong — the operator session may legitimately be aal1 (or the check may
 * error) while the act-as token is valid, which was bouncing the two Client
 * Book actions (Open / Map structure) to /auth/mfa-verify right after a
 * successful mint. We therefore skip the step-up gate ONLY while act-as is
 * `active`. An `expired` act-as is still NOT excepted from the step-up gate.
 * This narrows nothing for genuine operator/portal sessions (status `none` →
 * full gate as before).
 *
 * Act-as EXPIRY (2026-09-03, ruling 6 — register rows PPL-03 and BOW-02): what
 * an expired act-as used to do was "exit cleanly", meaning the first data call
 * threw ACT_AS_EXPIRED, cleared the store, and the firm-staff branch below then
 * navigated the operator to /admin/client-book. Read from the keyboard that is a
 * click that throws you out of the client, and two register rows were opened
 * against innocent screens because of it. A firm operator whose 15 minutes ran
 * out now gets `components/act-as-lapsed.tsx` in place: what happened, and one
 * click back in. The TTL is unchanged. See the block at the guard itself.
 *
 * Phase 9.5 (port): Each child route renders its own ScreenShell (sidebar +
 * topbar + main) from the Claude Design output. The old AppShellLayout is
 * bypassed here — child screens own their own layout for pixel-faithful
 * fidelity.
 *
 * Phase 9.6 may consolidate once multi-trust + admin sub-roles land.
 */

import { useEffect, useState } from 'react';
import { createFileRoute, Navigate, Outlet, useLocation } from '@tanstack/react-router';
import { useSession } from '../../lib/use-session';
import { preOnboardingRouteForSession } from '../../lib/auth-landing';
import { canAccess, isFirmStaffRole, isClientOnlyPath } from '../../lib/authz/route-policy';
import { currentCredentialStatus, lastKnownActAsSubject } from '../../lib/acting/context';
import { ActAsLapsed } from '../../components/act-as-lapsed';
import { useTenantMe } from '../../lib/api/tenants';
import { useTrusts } from '../../lib/api/trusts';
import { supabase } from '../../lib/supabase';
import { ImpersonationBanner } from '../../components/impersonation-banner';
import { FirmActBar } from '../../components/firm-act-bar';
import { PortalNotFound } from '../../components/error-pages/portal-not-found';
import { IdleLogoutWarning } from '../../components/idle-logout-warning';

export const Route = createFileRoute('/app')({
  component: AppLayout,
  // 2026-07-11 (first-touch rehearsal finding #14): an unmatched /app/* path
  // (e.g. /app/people) resolved to TanStack Router's bare unstyled
  // `<p>Not Found</p>` fallback — the root notFoundComponent
  // (error-pages/not-found.tsx) doesn't fire for a route nested this deep,
  // and even if it did it renders the marketing shell, wrong chrome for a
  // signed-in session. Wiring one here renders the actual portal chrome.
  notFoundComponent: () => <PortalNotFound />,
});

type AalState =
  | { status: 'checking' }
  | { status: 'ok' }
  | { status: 'needs-step-up' }
  /** No verified factor of any kind on the account, so there is nothing to
   *  step up TO. The user enrols one before the portal renders. */
  | { status: 'needs-enrolment' };

function AppLayout() {
  const { session, role, loading } = useSession();
  const pathname = useLocation({ select: (l) => l.pathname });
  const [aal, setAal] = useState<AalState>({ status: 'checking' });

  // Cross-device onboarding-resume gate (2026-07-04, walkthrough finding #11):
  // a b2c 'user' who provisioned their tenant on one device (provisioning
  // happens on FIRST entering /onboarding/individual, before the payment/MFA
  // steps) and then signs in on ANOTHER device passes the tenant_id gate below
  // and landed on the dashboard with onboarding unfinished. Signals for "still
  // mid-onboarding": the tenant is SELF-MANAGED (a firm-created A2 client
  // member must NOT be hijacked into self-onboarding — their bare tenant is
  // the firm's to populate) AND the tenant has ZERO trusts (finishing the
  // structure step always files at least one). Queries are enabled only for
  // exactly that session shape and React Query dedupes them with the
  // dashboard's own reads, so onboarded users pay nothing extra.
  // ARC-01: kind + liveness of any minted credential, from the acting context's
  // one sync read (never a store function — the independent verifier caught
  // this guard reading the stores' raw status helpers through a re-export the
  // lint could not see). Read once per render; both gates below share it.
  const credential = currentCredentialStatus();
  const actingAsClient = credential.kind === 'act-as' && credential.status === 'active';
  const impersonating = credential.kind === 'impersonation' && credential.status === 'active';
  const resumeCandidate =
    !loading &&
    !!session &&
    role === 'user' &&
    !!session.user?.app_metadata?.tenant_id &&
    !actingAsClient;
  const tenantQ = useTenantMe({ enabled: resumeCandidate });
  const trustsQ = useTrusts({ enabled: resumeCandidate });

  useEffect(() => {
    if (loading || !session) {
      // Reset when the session goes away so a future sign-in re-runs the check.
      setAal({ status: 'checking' });
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (cancelled) return;
      if (error || !data) {
        // Fail CLOSED: if the check itself errors, send the user to the MFA
        // verify screen rather than rendering the protected app. Supabase
        // enforces aal2 server-side for sensitive operations (the real
        // backstop), and mfa-verify handles the no-factor-enrolled case with a
        // message rather than a dead screen, so this cannot trap a user: it
        // just refuses to silently drop the gate.
        setAal({ status: 'needs-step-up' });
        return;
      }
      // `nextLevel` is 'aal2' exactly when the account has at least one
      // VERIFIED factor (@supabase/auth-js 2.105.4, GoTrueClient
      // `_getAuthenticatorAssuranceLevel`: it reads the user's factor list and
      // raises nextLevel only if a verified one is present). That covers a
      // passkey as well as an authenticator, which is what Paul's 2026-08-18
      // ruling asks for, and it needs no second round trip.
      //
      // So there are three states, not two, and this guard used to collapse
      // the third into 'ok':
      //   aal1 / aal2  a factor exists, this session has not passed it  -> verify
      //   aal1 / aal1  NO factor exists                                 -> enrol
      //   aal2 / *     already stepped up                               -> ok
      // The middle row let a zero-factor account live in the portal on a
      // password alone, indefinitely. /admin has always bounced its zero-factor
      // staff to the enrol screen (routes/admin/_layout.tsx), and the MFA
      // recovery flow deliberately leaves accounts with zero factors, so this
      // is now the landing state of every completed recovery.
      const needsStepUp = data.currentLevel === 'aal1' && data.nextLevel === 'aal2';
      const noFactor = data.currentLevel === 'aal1' && data.nextLevel === 'aal1';
      setAal({
        status: needsStepUp ? 'needs-step-up' : noFactor ? 'needs-enrolment' : 'ok',
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [session, loading]);

  if (loading) return null;

  if (!session) {
    return <Navigate to="/auth/login" />;
  }

  // AUDITOR-01 / S-2.1: an external auditor never belongs on the trustee app —
  // bounce them to their read-only surface before the protected /app shell
  // renders. /audit runs its own session + MFA gate, so this drops no check.
  // Allow-set sourced from the central route-policy (canAccess('app') is false
  // only for external-auditor).
  if (!canAccess('app', role)) {
    return <Navigate to="/audit" />;
  }

  // Minted-token bypass: while the effective credential on /app is a SHORT-LIVED
  // SERVER-MINTED token rather than the operator's own Supabase session, the
  // operator-session gates below must not fire. There are two such tokens and
  // they are the same mechanism (firm-act-store.ts says so in its own header):
  //
  //   • act-as       — a firm administering a client (everyday work).
  //   • impersonation — a super_admin reproducing an issue from a user's seat
  //                     (break-glass, audited against both parties).
  //
  // Both are minted off a stepped-up operator SERVER-SIDE, so re-running the
  // operator-session AAL check here is redundant; it was bouncing Open / Map
  // structure to /auth/mfa-verify right after a successful act-as mint.
  //
  // 2026-08-25 — IMPERSONATION WAS OMITTED FROM THIS BYPASS, WHICH KILLED THE
  // WHOLE FEATURE. superadmin/tenants/$id-screen.tsx navigates to /app/dashboard
  // the moment POST /v1/superadmin/impersonate/start succeeds; `super_admin` is a
  // FIRM_STAFF role, so the exit-bounce below fired and returned the operator to
  // /admin/client-book every time. The ImpersonationBanner is mounted on /app/*
  // (see the render branch at the bottom of this file) and so could never mount,
  // taking "Leave impersonation" with it — while api() was already sending the
  // live impersonation JWT. Caught by e2e/34-superadmin-flows.
  //
  // `expired` is NOT excepted for either token. On the first data call the api()
  // client surfaces ACT_AS_EXPIRED → redirect to /admin/client-book, and an
  // expired impersonation is cleared there too.
  //
  // ⚠ WHERE that impersonation clear happens MOVED, and this comment named the
  // old address for a while. It used to be a side effect of reading
  // `activeImpersonationToken()`; since ARC-01 T4 that accessor has no
  // production callers at all, and `api()` performs the clear explicitly before
  // re-deriving the acting context. The guarantee is unchanged; the owner is
  // not. If that explicit clear is ever removed, an expired impersonation sticks
  // forever and this gate silently stops being reachable.
  // (`actingAsClient` / `impersonating` are read once, above, from the acting
  // context's sync credential read.)
  const onMintedToken = actingAsClient || impersonating;

  // ── THE CLIENT'S OWN FOUR SCREENS (asked 22, 25 and 25 Aug) ────────────────
  //
  // Inbox, Security, Firm access and Privacy belong to the client alone. A firm
  // administering them must not arrive on any of the four, by link, by typed
  // URL, by bookmark or by back button. See CLIENT_ONLY_PATHS in
  // lib/authz/route-policy.ts for the ruling and the three times it was asked.
  //
  // Deliberately keyed on `actingAsClient` and NOT on `onMintedToken`:
  // impersonation is a super_admin sitting in the user's own seat to reproduce
  // what the user sees, audited against both parties. Blinding it would defeat
  // the point of the feature and would have re-broken what 25 August fixed.
  //
  // Bounce to the client's overview, which is one of the two surfaces Paul named
  // as legitimate — not to /admin, which would eject a firm mid-task for
  // clicking a stale bookmark.
  if (actingAsClient && isClientOnlyPath(pathname)) {
    return <Navigate to="/app/dashboard" replace />;
  }

  // ── THE 15 MINUTES RAN OUT (PPL-03 / BOW-02, ruling 6 of 2026-09-03) ────────
  //
  // Widen the guard, keep the TTL. Until now the firm-staff branch below owned
  // this state and it could not tell two very different things apart:
  //
  //   · "Exit to clients" was pressed, or /admin's beforeLoad cleared the
  //     context on the way into the console. The operator MEANT to leave, and
  //     sending them to /admin/client-book is right.
  //   · The act-as token simply ran out under a screen they were reading. The
  //     operator meant nothing at all, and sending them anywhere reads as the
  //     screen throwing them out. That is PPL-03 (People & access) and BOW-02
  //     (Beneficial ownership to Beneficiaries): one mechanism, two rows, and a
  //     firm operator convinced the product had a bug.
  //
  // Both arrive here identically, because `lib/api/client.ts` CLEARS the store
  // as it throws ACT_AS_EXPIRED, so by the next render the credential usually
  // reads 'session', not 'act-as'/'expired'. The fact that separates them is the
  // sticky subject in `lib/acting/last-act-as.ts`, which records every act-as
  // the session ever held and is deliberately NOT cleared by an expiry. The
  // deliberate exits ARE now required to forget it (sign-out and /admin's
  // beforeLoad both call `forgetLastKnownActAsSubject()`), which is what makes
  // its presence here mean "ran out" rather than "left".
  //
  // Gated on `isFirmStaffRole` so nothing changes for a portal user who somehow
  // holds a stale act-as record: they still owe their own step-up below.
  const actAsRanOut =
    isFirmStaffRole(role) &&
    (credential.kind === 'act-as'
      ? credential.status === 'expired'
      : credential.kind === 'session' && lastKnownActAsSubject() !== null);
  if (actAsRanOut) {
    return <ActAsLapsed />;
  }

  if (!onMintedToken) {
    // Firm-staff-on-/app-without-act-as is an ANOMALY, not an MFA problem
    // (2026-07-03, QA P1 — the "Exit to clients" bounce). Firm staff only ever
    // reach /app through an act-as session; the moment that context clears they
    // belong in their firm console, NOT on the b2c step-up screen. This is why
    // the previous await-navigate-then-clear fix in firm-act-bar.tsx couldn't
    // hold: /admin/_layout's beforeLoad calls clearActAsContext() (act-as-
    // context.ts), which clears the SAME firm-act store this guard reads, so
    // during the awaited nav the store flips to `none`, the still-mounted
    // AppLayout re-renders, sees no act-as + an aal1 operator session, and its
    // step-up bounce wins the race before /admin commits. Routing firm staff to
    // their admin home HERE fixes the whole class regardless of that timing —
    // and any other path that strands a firm operator on /app sans act-as.
    // A genuine b2c portal `user` (or unknown role) is NOT firm staff, so it
    // still owes its aal2 promotion and falls through to the mfa-verify bounce.
    if (isFirmStaffRole(role)) {
      return <Navigate to="/admin/client-book" replace />;
    }

    // Pre-onboarding session (no tenant yet) never belongs on /app (2026-07-03,
    // QA noise fix): login routes purely by role (landingRouteForRole), so a
    // brand-new b2c account — confirmed email, no UserDoc, no tenant — landed on
    // /app/dashboard, which mounted and fired GET /v1/dashboard/summary (plus
    // the trusts/me reads) into a guaranteed 404 before anything sent the user
    // to onboarding. Gate HERE, synchronously off the hydrated session's
    // app_metadata (no extra fetch, no loading flash for onboarded users, whose
    // JWT carries tenant_id — the onboarding confirm refreshSession()s before
    // handing off to /app/dashboard). Persona mirrors routes/auth/callback.tsx:
    // signup's user_metadata.account_type 'firm' → the firm bootstrap; anything
    // else (individual / absent / legacy) → the b2c doc-first flow. Inside the
    // !onMintedToken branch on purpose: under an active act-as / impersonation
    // the effective credential is the minted token and the operator's own
    // tenancy is moot.
    if (!session.user?.app_metadata?.tenant_id) {
      // Persona resolved by the shared helper (auth-landing.ts) — same mapping
      // the confirm callback and the marketing landing safety-net use.
      return <Navigate to={preOnboardingRouteForSession(session)} replace />;
    }

    // Cross-device resume (finding #11, see the hook block above): tenant
    // exists but onboarding is unfinished → resume it. Waiting on the two
    // queries mirrors the AAL 'checking' null-render just below (no dashboard
    // flash); a query ERROR falls through open — never lock a user out of
    // their portal because tenants/me hiccuped.
    if (resumeCandidate) {
      if (tenantQ.isLoading || trustsQ.isLoading) return null;
      const selfManaged = tenantQ.data?.self_managed === true;
      const trustCount = trustsQ.data?.trusts?.length ?? 0;
      if (selfManaged && trustCount === 0 && !trustsQ.isError) {
        return <Navigate to="/onboarding/individual" replace />;
      }
    }

    if (aal.status === 'checking') return null;

    // Enrolment is checked BEFORE the step-up bounce, the same order
    // routes/admin/_layout.tsx uses and for the same reason: an account with no
    // factor sent to /auth/mfa-verify arrives at a screen with nothing to
    // verify against.
    if (aal.status === 'needs-enrolment') {
      return <Navigate to="/auth/mfa-enroll" search={{ next: '/app/dashboard' } as never} />;
    }

    if (aal.status === 'needs-step-up') {
      return <Navigate to="/auth/mfa-verify" />;
    }
  }

  // Bare /app has no index route (routeTree.gen.ts is hand-maintained; the
  // dashboard lives at /app/dashboard), so its <Outlet /> would render blank.
  // Many flows redirect HERE expecting it to land somewhere — the auth
  // callback for an already-onboarded sign-up (routes/auth/callback.tsx),
  // password reset (routes/auth/reset-password.tsx), and the superadmin /
  // admin / audit layouts when their surface is gated off. Send /app → the
  // dashboard so none of them strand the user on an empty shell.
  // (2026-06-11 shakedown: this was a blank-screen bug on every onboarded
  // signup callback + the gated-superadmin redirect.)
  if (pathname === '/app' || pathname === '/app/') {
    return <Navigate to="/app/dashboard" />;
  }

  return (
    <>
      <ImpersonationBanner />
      <FirmActBar />
      {/* Beno feedback (2026-07-15): idle auto-logout — 15 min total, 60 s
          warning at the 14-min mark. Mounted here (not __root.tsx) so it only
          ever tracks activity on the authenticated /app surface, never the
          public marketing pages or the /auth/* screens — this branch only
          renders once every session/AAL/onboarding gate above has passed. */}
      <IdleLogoutWarning />
      <Outlet />
    </>
  );
}

// Default export of the guard component so it can be rendered in isolation by
// the layout-guard test (mirrors routes/admin/_layout.tsx). The route itself
// is wired via the named `Route` export above; this default is test-only.
export default AppLayout;
